import { useState, useEffect } from "react";

export interface BlogPost {
  title: string;
  excerpt: string;
  content: string;
  image: string;
  date: string;
  author: string;
  category: string;
  link: string;
  slug: string;
}

const defaultPosts: BlogPost[] = [
  {
    title: "Ease Canine Anxiety with Scent Work",
    excerpt:
      "Discover how scent work can reduce anxiety, build confidence, and bond with your dog.",
    content:
      "<p>Discover how scent work can reduce anxiety, build confidence, and bond with your dog.</p>",
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/funnel-ai-production/blog-ai/LybUg0eKBAfPYJVWJI73/cover-1782421362786-7ic7dk.png",
    date: "25 June 2026",
    author: "Chrissy Moore",
    category: "Dog Training Tips",
    link: "/blog",
    slug: "ease-canine-anxiety-with-scent-work",
  },
  {
    title: "Understanding Dog Reactivity: Beyond Bad Behavior",
    excerpt:
      "Reactivity is often a sign of fear or overwhelm. Let's dive into what's really happening behind the barking and lunging.",
    content:
      "<p>Reactivity is often a sign of fear or overwhelm. Let's dive into what's really happening behind the barking and lunging.</p>",
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/funnel-ai-production/blog-ai/LybUg0eKBAfPYJVWJI73/cover-1782420912790-mc12hd.png",
    date: "25 June 2026",
    author: "Chrissy Moore",
    category: "Dog Training Tips",
    link: "/blog",
    slug: "understanding-dog-reactivity",
  },
  {
    title: "5 Tips for Puppy Training Success",
    excerpt:
      "Starting your puppy journey? Here are five essential tips to build a strong foundation for your new best friend.",
    content:
      "<p>Starting your puppy journey? Here are five essential tips to build a strong foundation for your new best friend.</p>",
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://storage.googleapis.com/funnel-ai-production/blog-ai/LybUg0eKBAfPYJVWJI73/cover-1782420696300-wxqh22.png",
    date: "25 June 2026",
    author: "Chrissy Moore",
    category: "Dog Training Tips",
    link: "/blog",
    slug: "5-tips-for-puppy-training-success",
  },
];

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>(defaultPosts);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          "https://rss-link.com/feed/LybUg0eKBAfPYJVWJI73?blogId=E2HgjtXkRGnlGJB08RWs&limit=100&loadContent=true",
        );
        if (!res.ok) throw new Error("Failed to fetch RSS");
        const text = await res.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        const items = xmlDoc.querySelectorAll("item");

        if (items.length > 0) {
          const parsedPosts = Array.from(items).map((item, index) => {
            const title = item.querySelector("title")?.textContent || "";
            const link = item.querySelector("link")?.textContent || "/blog";
            const pubDate = item.querySelector("pubDate")?.textContent || "";
            const category =
              item.querySelector("category")?.textContent || "Blog";

            let image = "";
            const encoded =
              item.getElementsByTagName("content:encoded")[0]?.textContent ||
              "";
            const imgMatch = encoded.match(/<img[^>]+src=["']([^"']+)["']/i);
            if (imgMatch) {
              image = imgMatch[1];
            } else if (defaultPosts[index]?.image) {
              image = defaultPosts[index].image;
            }

            const description =
              item.querySelector("description")?.textContent || "";
            let excerpt = description.replace(/<[^>]*>?/gm, "").trim();
            if (excerpt.length > 150) {
              excerpt = excerpt.substring(0, 150) + "...";
            }

            const slug = title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)+/g, "");

            let content = encoded || description || "";
            if (content) {
              const doc = new DOMParser().parseFromString(content, "text/html");
              const walker = document.createTreeWalker(
                doc.body,
                NodeFilter.SHOW_TEXT,
                null,
              );
              let node;
              const nodesToRemove = [];
              while ((node = walker.nextNode())) {
                if (node.nodeValue?.includes("Custom HTML/CSS/JAVASCRIPT")) {
                  nodesToRemove.push(node.parentElement);
                }
              }
              nodesToRemove.forEach((n) => {
                let target = n;
                while (
                  target &&
                  target.parentElement &&
                  target.parentElement !== doc.body &&
                  target.tagName !== "DIV"
                ) {
                  target = target.parentElement;
                }
                if (target) target.remove();
              });

              // Remove code blocks and their intro text
              const preTags = doc.querySelectorAll("pre");
              preTags.forEach((pre) => pre.remove());

              const pTags = doc.querySelectorAll("p");
              pTags.forEach((p) => {
                if (
                  p.textContent?.includes("Python-style sketch") ||
                  p.textContent?.includes(
                    "behavior understanding might look in code",
                  )
                ) {
                  p.remove();
                }
              });

              content = doc.body.innerHTML;
            }

            return {
              title,
              excerpt: excerpt || defaultPosts[index]?.excerpt || "",
              content: content,
              image:
                image ||
                "https://vibe.filesafe.space/1780604500184904220/assets/041d097c-397c-422c-b3f8-2f051b9d61db.jpg",
              date: pubDate
                ? new Date(pubDate).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "",
              author: "Chrissy Moore",
              category,
              link,
              slug,
            };
          });
          setPosts(parsedPosts);
        }
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return { posts, isLoading };
}
